import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Box, TextField, IconButton, Typography, CircularProgress } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import toast from 'react-hot-toast';
import { noteService, Note, NoteEntityType } from '../../services/noteService';
import { useAuth } from '../../context/AuthContext';

interface NotesListProps {
  entityType: NoteEntityType;
  entityId: number;
  maxHeight?: number | string;
}

const fmt = (iso: string) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
};

const NotesList: React.FC<NotesListProps> = ({ entityType, entityId, maxHeight = 320 }) => {
  const { user } = useAuth();
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editText, setEditText] = useState('');
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const listEndRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await noteService.getNotes(entityType, entityId);
      setNotes(data);
    } catch {
      // entity may not exist yet (e.g. new task) — keep empty
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { listEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [notes.length]);

  const handleAdd = async () => {
    const value = text.trim();
    if (!value) return;
    setSaving(true);
    try {
      const created = await noteService.createNote({ entity_type: entityType, entity_id: entityId, text: value });
      setNotes((prev) => [...prev, created]);
      setText('');
    } catch {
      toast.error('Could not add note');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async (id: number) => {
    const value = editText.trim();
    if (!value) return;
    try {
      const updated = await noteService.updateNote(id, { text: value });
      setNotes((prev) => prev.map((n) => (n.id === id ? updated : n)));
      setEditingId(null);
      setEditText('');
    } catch {
      toast.error('Could not save note');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Box sx={{ p: 1.5, maxHeight, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={20} /></Box>
        ) : notes.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>No notes yet.</Typography>
        ) : (
          notes.map((n) => {
            const mine = !!user && n.created_by === user.id;
            const isEditing = editingId === n.id;
            return (
              <Box
                key={n.id}
                sx={{ alignSelf: mine ? 'flex-end' : 'flex-start', maxWidth: '85%' }}
                onMouseEnter={() => setHoveredId(n.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexDirection: mine ? 'row' : 'row-reverse' }}>
                  {mine && !isEditing && (
                    <IconButton
                      size="small"
                      onClick={() => { setEditingId(n.id); setEditText(n.text); }}
                      sx={{
                        p: 0.25, fontSize: 14, lineHeight: 1, color: 'text.secondary', flexShrink: 0,
                        transition: 'opacity 0.15s',
                        opacity: hoveredId === n.id ? 1 : 0,
                        pointerEvents: hoveredId === n.id ? 'auto' : 'none',
                        '&:hover': { color: 'primary.main' },
                      }}
                      title="Edit note"
                    >
                      ✎
                    </IconButton>
                  )}
                  <Box sx={{ bgcolor: mine ? '#e3f2fd' : '#f1f3f4', borderRadius: 2, px: 1.5, py: 1 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.25 }}>
                      {mine ? 'You' : (n.created_by_name || 'Unknown')} · {fmt(n.created_at)}
                    </Typography>
                    {isEditing ? (
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                        <TextField
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          size="small" multiline fullWidth
                        />
                        <IconButton size="small" onClick={() => handleSaveEdit(n.id)}><SendIcon fontSize="small" /></IconButton>
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{n.text}</Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })
        )}
        <div ref={listEndRef} />
      </Box>
      <Box sx={{ display: 'flex', gap: 1, p: 1, borderTop: '1px solid', borderColor: 'divider' }}>
        <TextField
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAdd(); } }}
          placeholder="Add a note…"
          size="small" fullWidth multiline maxRows={4}
        />
        <IconButton color="primary" onClick={handleAdd} disabled={saving || !text.trim()}>
          <SendIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default NotesList;
